import { useMemo } from 'react';
import { Button, Skeleton } from '@/components/ui';
import { useAppAuth } from '@/lib/useAppAuth';
import { useOpenLogin } from '@/store/authModalStore';
import { useCommentsMe, useEventComments } from '@/lib/comments/api';
import { formatNotificationTime } from '@/lib/notifications/format-relative-time';
import { CommentComposer } from './CommentComposer';
import { CommentItem } from './CommentItem';

export interface CommentsSectionProps {
  eventId: string;
}

/**
 * The event page Comments section (fills the former disabled shell in
 * `MarketSections`). Reads are public; the composer requires sign-in, and
 * posting/liking is additionally KYC-gated server-side (the 403 interceptor
 * opens the gate and replays). Comment-banned callers see an inline notice.
 */
export function CommentsSection({ eventId }: CommentsSectionProps) {
  const { isAuthenticated } = useAppAuth();
  const openLogin = useOpenLogin();

  const list = useEventComments(eventId);
  const { data: me } = useCommentsMe(eventId, { query: { enabled: isAuthenticated } });

  const comments = list.data?.pages.flatMap((p) => p.items) ?? [];
  const likedIds = useMemo(() => new Set(me?.likedCommentIds ?? []), [me?.likedCommentIds]);
  // The api only returns a still-active ban (expired ⇒ null) — no client clock math.
  const bannedUntil = me?.commentBannedUntil ?? null;
  // The far-future sentinel means a permanent ban — showing its date is noise.
  const permanentBan = bannedUntil !== null && new Date(bannedUntil).getUTCFullYear() >= 9999;

  return (
    <div className="flex flex-col gap-4">
      {!isAuthenticated ? (
        <div className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface px-4 py-3">
          <span className="text-sm text-muted">Sign in to join the discussion</span>
          <Button size="sm" onClick={openLogin}>
            Sign In
          </Button>
        </div>
      ) : bannedUntil ? (
        <p className="rounded-card border border-border bg-surface px-4 py-3 text-sm text-muted">
          {permanentBan
            ? 'You are currently not allowed to comment.'
            : `You can't comment until ${formatNotificationTime(bannedUntil)}.`}
        </p>
      ) : (
        <CommentComposer eventId={eventId} />
      )}

      {list.isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-2.5">
              <Skeleton className="size-8 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3.5 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : list.isError ? (
        <p className="text-sm text-muted">Comments couldn’t be loaded — try refreshing.</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted">No comments yet — be the first to share your take.</p>
      ) : (
        // Bounded scroll region so a long thread scrolls in place instead of
        // pushing the rest of the market page down; "Show more comments" (below)
        // still appends into it.
        <div className="flex max-h-[420px] flex-col gap-4 overflow-y-auto pr-1">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              eventId={eventId}
              currentUserId={me?.userId ?? null}
              likedIds={likedIds}
            />
          ))}
        </div>
      )}

      {list.hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="self-center text-purple"
          onClick={() => void list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? 'Loading…' : 'Show more comments'}
        </Button>
      )}
    </div>
  );
}
